import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { validateToken } from '../slices/authSlice';

const AuthChecker = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token, isTokenValidating } = useSelector(state => state.auth);
    const intervalRef = useRef(null);

    // Vérification initiale du token
    useEffect(() => {
        if (token) {
            dispatch(validateToken());
        }
    }, [dispatch, token]);

    // Vérification périodique du token (toutes les 5 minutes)
    useEffect(() => {
        if (token) {
            // Vérifier immédiatement
            dispatch(validateToken());

            // Puis vérifier toutes les 5 minutes
            intervalRef.current = setInterval(() => {
                dispatch(validateToken());
            }, 5 * 60 * 1000); // 5 minutes

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }
    }, [dispatch, token]);

    // Vérification lors du retour de focus sur la page (avec débounce)
    useEffect(() => {
        let focusTimeout = null;
        
        const handleVisibilityChange = () => {
            if (!document.hidden && token && !isTokenValidating) {
                // Débounce de 2 secondes pour éviter les appels multiples
                if (focusTimeout) clearTimeout(focusTimeout);
                focusTimeout = setTimeout(() => {
                    dispatch(validateToken());
                }, 2000);
            }
        };

        const handleFocus = () => {
            if (token && !isTokenValidating) {
                // Débounce de 2 secondes pour éviter les appels multiples
                if (focusTimeout) clearTimeout(focusTimeout);
                focusTimeout = setTimeout(() => {
                    dispatch(validateToken());
                }, 2000);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            if (focusTimeout) clearTimeout(focusTimeout);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [dispatch, token, isTokenValidating]);

    // Redirection si plus de token
    useEffect(() => {
        const currentPath = window.location.pathname;
        
        // Chemins publics exacts
        const publicPaths = [
            '/', 
            '/login', 
            '/register', 
            '/reset-password-request',
            '/about', 
            '/okrdv', 
            '/demo',
            '/guides', 
            '/mentions-legales',
            '/pricing',
            '/methode',
            '/barometre',
            '/comparatifs',
            '/success',
            '/cancel',
        ];
        
        const publicPathPatterns = [
            /^\/guide\//,
            /^\/vs\//,
            /^\/reset-password\//,
            /^\/r\//,
        ];
        
        // Vérifier si le chemin est public (exact ou pattern)
        const isPublicPath = publicPaths.includes(currentPath) || 
                            publicPathPatterns.some(pattern => pattern.test(currentPath));
        
        if (!token && !isPublicPath) {
            navigate(currentPath === '/subscription' ? '/login?redirect=/subscription' : '/login');
        }
    }, [token, navigate]);

    return null; // Ce composant ne rend rien visuellement
};

export default AuthChecker;
